import React from 'react';
import { costModel, pricingConfig, warrantyConfig } from '../../data/pricing';
import type { WarrantyTier } from '../../data/pricing';
import { addonUnitPrice, formatMoney, priceBundle, priceIndividual, warrantyLabel } from '../../utils/pricing';
import { contributionMargin } from '../../utils/margin';
import { PendingNote } from '../../components/ui/PendingNote';
type Row = {
  offer: string;
  lights: number;
  price: number;
  tier: WarrantyTier;
  incremental?: boolean;
};
export function PricingModel() {
  const unit = pricingConfig.unitPrice;
  const rows: Row[] = [...pricingConfig.volumeTiers.map(t => ({
    offer: `Build Your Own · ${t.minQty} light${t.minQty > 1 ? 's' : ''}`,
    lights: t.minQty,
    price: priceIndividual(t.minQty).total,
    tier: warrantyConfig.individual
  })), ...pricingConfig.bundles.map(b => ({
    offer: b.name,
    lights: b.qty,
    price: priceBundle(b).total,
    tier: b.warranty
  })), {
    offer: 'Bundle add-on light (incremental)',
    lights: 1,
    price: addonUnitPrice(),
    tier: 'oneYear' as WarrantyTier,
    incremental: true
  }];
  const costRows: [string, string][] = [['Unit cost (light + base, landed)', fmt(costModel.unitCost)], ['Packaging per order', fmt(costModel.packagingCostPerOrder)], ['Packaging per unit', fmt(costModel.packagingCostPerUnit)], ['Shipping — first light', fmt(costModel.shippingCostPerOrder)], ['Shipping — each additional light', fmt(costModel.shippingCostPerAdditionalUnit)], ['Payment processing %', costModel.paymentProcessingPct === null ? '' : `${costModel.paymentProcessingPct}%`], ['Payment processing fixed', fmt(costModel.paymentProcessingFixed)], ['Warranty allowance — 1-year', pct(costModel.warrantyAllowancePct.oneYear)], ['Warranty allowance — lifetime', pct(costModel.warrantyAllowancePct.lifetime)]];
  return <div className="mx-auto max-w-site px-5 py-14 md:px-8 md:py-20" data-id="element-1084" data-mp="y4hcnaab5a8h">
      <h1 className="font-display text-5xl font-semibold uppercase md:text-6xl" data-id="element-1085" data-mp="tad4wot7wy6c">Pricing & Margin Model</h1>
      <p className="mt-4 max-w-2xl text-titanium" data-id="element-1086" data-mp="2nkjwalaijupz">
        Internal development view. Every price below is <strong className="text-white" data-id="element-1087" data-mp="2si39rize03gv">provisional</strong> and read from{' '}
        <code className="text-ice" data-id="element-1088" data-mp="2dt741sksz2jf">data/pricing.ts</code>. Contribution margins appear only once confirmed costs are entered.
      </p>
      <div className="mt-6 max-w-3xl" data-id="element-1089" data-mp="2iqyuei5sugli">
        <PendingNote tone="block" data-id="element-1090" data-mp="24sr0kj427vek">
          Prices status: {pricingConfig.pricesApproved ? 'Approved' : 'PROVISIONAL — not approved by owner'}. Promo:{' '}
          {pricingConfig.promo.active ? `${pricingConfig.promo.pctOff}% active` : 'none'}.
        </PendingNote>
      </div>

      <div className="mt-12 overflow-x-auto" data-id="element-1091" data-mp="1ztxp0w8c2eh1">
        <table className="w-full min-w-[820px] text-left text-sm" data-id="element-1092" data-mp="1zuz3zbsgzl41">
          <thead data-id="element-1093" data-mp="3p8lximdszc9s">
            <tr className="border-b border-titanium/25 text-titanium/80" data-id="element-1094" data-mp="3pa6sgd414x86">
              {['Offer', 'Lights', 'Selling price', 'Per light', 'Discount vs. individual', 'Warranty', 'Contribution margin'].map(h => <th key={h} scope="col" className="pb-3 pr-4 font-medium" data-id="element-1095" data-mp="1ilekfe9osgzn">{h}</th>)}
            </tr>
          </thead>
          <tbody data-id="element-1096" data-mp="1djxq61lpjss1">
            {rows.map(r => {
            const perLight = r.price / r.lights;
            const discount = Math.round((1 - perLight / unit) * 1000) / 10;
            const mgn = contributionMargin(r.price, r.lights, r.tier, r.incremental);
            return <tr key={r.offer} className="border-b mf-hairline align-top" data-id="element-1097" data-mp="21d7frfc8aeaj">
                  <th scope="row" className="py-3 pr-4 font-medium text-white" data-id="element-1098" data-mp="1x9huayquoctq">{r.offer}</th>
                  <td className="py-3 pr-4" data-id="element-1099" data-mp="2b90r4ve7o1qq">{r.lights}</td>
                  <td className="py-3 pr-4" data-id="element-1100" data-mp="269xxs87lfjyl">{formatMoney(r.price)}</td>
                  <td className="py-3 pr-4" data-id="element-1101" data-mp="268mo5rrgvhzk">{formatMoney(perLight)}</td>
                  <td className="py-3 pr-4" data-id="element-1102" data-mp="gvrp1onzviii">{discount > 0 ? `${discount}%` : '—'}</td>
                  <td className="py-3 pr-4 text-titanium" data-id="element-1103" data-mp="bxq2gtrd112a">{warrantyLabel(r.tier).label}</td>
                  <td className="py-3" data-id="element-1104" data-mp="dcsiiz7zhu43">
                    {mgn.status === 'ok' ? <span className="text-white" data-id="element-1105" data-mp="ibnbuv3kv12g">{formatMoney(mgn.contribution)} ({mgn.contributionPct}%)</span> : <span className="text-ice/80" data-id="element-1106" data-mp="n9otrcp9oucc">Needs cost data ({mgn.missing.length} inputs)</span>}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      <h2 className="mt-16 font-display text-3xl font-semibold uppercase" data-id="element-1107" data-mp="3vr1rbkd2wkw8">Cost inputs</h2>
      <table className="mt-5 w-full max-w-2xl text-left text-sm" data-id="element-1108" data-mp="4ljuoijk1xjv">
        <tbody data-id="element-1109" data-mp="4j73rfvtkcuo">
          {costRows.map(([k, v]) => <tr key={k} className="border-b mf-hairline" data-id="element-1110" data-mp="9d1xlc55287h">
              <th scope="row" className="py-3 pr-4 font-medium text-titanium" data-id="element-1111" data-mp="3820ezm14kplo">{k}</th>
              <td className="py-3 text-right" data-id="element-1112" data-mp="37zwd19mse0o1">{v || <span className="text-ice/80" data-id="element-1113" data-mp="3d0k0jlbt3zdi">Not confirmed</span>}</td>
            </tr>)}
        </tbody>
      </table>
      <p className="mt-4 max-w-2xl text-xs text-titanium/60" data-id="element-1114" data-mp="1u4o0hicgm0da">
        Add-on margin is calculated as incremental: no per-order packaging, shipping or fixed processing fee, but it still
        carries product cost, per-unit packaging/shipping, variable processing and warranty allowance.
      </p>
    </div>;
}
function fmt(v: number | null) {
  return v === null ? '' : formatMoney(v);
}
function pct(v: number | null) {
  return v === null ? '' : `${v}%`;
}